import React, { Suspense, lazy } from "react";
import { LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import "../../../../assets/ui/public-controls.js";
import {
  NUVELOR_APP_URL,
  NUVELOR_SOCIAL_LINKS,
  nuvelorPublicUrl,
} from "../../../../assets/ui/public-navigation.js";
import logo from "../assets/brand/nuvelor-logo.jpg";
import { SUPPORTED_LOCALES } from "../i18n/supportedLocales";
import { useWalletConnect } from "../WalletConnectContext";
import AppBootScreen from "./AppBootScreen";
import MarketDocumentMetadata from "./MarketDocumentMetadata";
import WalletConnectControl from "./WalletConnectControl";
import "./MarketStandaloneLayout.css";

const MarketSectionRoute = lazy(() => import("./MarketSection"));

function shortAccount(accountId) {
  const value = String(accountId || "").trim();
  if (value.length <= 18) return value;
  return `${value.slice(0, 8)}…${value.slice(-6)}`;
}

export default function MarketStandaloneLayout() {
  const { t, i18n } = useTranslation("market");
  const { t: settingsT } = useTranslation("settings");
  const { accountId, disconnect } = useWalletConnect() || {};
  const activeLanguage = String(i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const activeLocale = SUPPORTED_LOCALES.find((locale) => locale.code === activeLanguage)
    || SUPPORTED_LOCALES.find((locale) => locale.code === "en")
    || SUPPORTED_LOCALES[0];

  const handleLanguageChange = async (next) => {
    localStorage.setItem("app_lang", next);
    await i18n.changeLanguage(next);
  };

  const publicLink = (section = "") => nuvelorPublicUrl(activeLanguage, section);

  return (
    <div className="market-standalone">
      <MarketDocumentMetadata />
      <header className="market-standalone__header">
        <div className="market-standalone__header-inner">
          <Link className="market-standalone__brand" to="/" aria-label="NUVELOR Market">
            <img src={logo} alt="" width="44" height="44" />
            <span>
              <strong>NUVELOR</strong>
              <small>{t("shell.productName")}</small>
            </span>
          </Link>

          <div className="market-standalone__wallet">
            {accountId ? (
              <>
                <Link
                  className="staking-wallet-chip market-standalone__account-link"
                  to="/account"
                  title={accountId}
                  aria-label={accountId}
                >
                  <span>{shortAccount(accountId)}</span>
                  <span className="staking-wallet-chip__dot" />
                </Link>
                <button
                  type="button"
                  className="market-standalone__icon-button"
                  onClick={() => disconnect?.()}
                  aria-label={t("shell.disconnect")}
                  title={t("shell.disconnect")}
                >
                  <LogOut size={18} aria-hidden="true" />
                </button>
              </>
            ) : (
              <WalletConnectControl />
            )}
          </div>
        </div>
      </header>

      <main className="market-standalone__main">
        <Suspense fallback={<AppBootScreen pathname="/" showBottomNavPlaceholder={false} />}>
          <Routes>
            <Route path="/" element={<MarketSectionRoute view="market" />} />
            <Route path="/account" element={<MarketSectionRoute view="account" />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      <footer className="market-standalone__footer">
        <div className="market-standalone__footer-inner">
          <div className="market-standalone__footer-content">
            <div className="market-standalone__footer-brand">
              <strong>NUVELOR</strong>
              <span>© 2026 · Next version of reality.</span>
            </div>

            <nav
              className="market-standalone__footer-links"
              aria-label={t("shell.footer.publicLinks")}
            >
              <a href={publicLink()}>{t("shell.footer.home")}</a>
              <a href={publicLink("game")}>{t("shell.footer.game")}</a>
              <a href={publicLink("nfts")}>{t("shell.footer.nfts")}</a>
              <a href={publicLink("economy")}>{t("shell.footer.economy")}</a>
              <a href={publicLink("guides")}>{t("shell.footer.guides")}</a>
              <a href={publicLink("roadmap")}>{t("shell.footer.roadmap")}</a>
              <a href={publicLink("about")}>{t("shell.footer.about")}</a>
              <a href={NUVELOR_APP_URL} target="_blank" rel="noopener noreferrer">
                {t("shell.footer.app")}
              </a>
            </nav>

            <nav
              className="market-standalone__footer-socials"
              aria-label={t("shell.footer.socialLinks")}
            >
              <a href={NUVELOR_SOCIAL_LINKS.telegram} target="_blank" rel="noopener noreferrer">
                Telegram
              </a>
              <a href={NUVELOR_SOCIAL_LINKS.community} target="_blank" rel="noopener noreferrer">
                {t("shell.footer.community")}
              </a>
              <a href={NUVELOR_SOCIAL_LINKS.x} target="_blank" rel="noopener noreferrer">
                X
              </a>
              <a href={NUVELOR_SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer">
                Instagram
              </a>
            </nav>
          </div>

          <div className="market-standalone__footer-controls">
            <nuvelor-language-picker
              current={activeLocale?.shortLabel || activeLanguage.toUpperCase()}
              label={t("shell.language")}
              direction="up"
            >
              {SUPPORTED_LOCALES.map((locale) => (
                <button
                  key={locale.code}
                  data-language-option
                  type="button"
                  role="menuitemradio"
                  aria-checked={locale.code === activeLanguage}
                  aria-label={locale.label}
                  onClick={() => handleLanguageChange(locale.code)}
                >
                  <strong>{locale.shortLabel || locale.code.toUpperCase()}</strong>
                  <small>{locale.label}</small>
                </button>
              ))}
            </nuvelor-language-picker>

            <nuvelor-theme-toggle
              label-light={settingsT("themeUseLight")}
              label-dark={settingsT("themeUseDark")}
            />
          </div>
        </div>
      </footer>
    </div>
  );
}
